import { useFormik } from 'formik'
import { useEffect } from 'react'
import { toast } from 'react-toastify'

import { Drawer, Icon, LabeledValue, Layout, Steps } from '@loadsmart/miranda-react'

import { RequestStatus } from 'core/domain/Request'

import { buildValidationSchema, type OpenDockWarehouseDetails } from '../../domain/Shipment'
import {
  type OpenDockWarehouseFormFieldValue,
  type OpenDockWarehouseFormFields,
} from '../../infra/ShipmentMapper'
import { formatScheduledAt } from '../../presenters/openDockAppointmentPresenter'
import { formatLoadTypeInfo } from '../../presenters/openDockLoadTypePresenter'
import { useCreateOpenDockWarehouseAppointment } from '../hooks/useCreateOpenDockWarehouseAppointment'
import { useShipmentAnalytics } from '../hooks/useShipmentAnalytics'
import { DynamicField, DynamicRefNumberField } from './DynamicField'
import { type ScheduleFormValues } from './Schedule'

export type FormValues = {
  formFields: OpenDockWarehouseFormFieldValue[] | undefined
  customFormFields: OpenDockWarehouseFormFieldValue[] | undefined
  refNumbers: string | null | undefined
}

function setFormValuesToWarehouseFormFields(
  warehouseFormFields: OpenDockWarehouseFormFields,
  formValues: FormValues
) {
  const warehouseFormFieldsCopy = structuredClone(warehouseFormFields)

  if (warehouseFormFieldsCopy.formFields && typeof formValues.formFields !== 'undefined') {
    warehouseFormFieldsCopy.formFields.forEach((field, index) => {
      field.value = formValues?.formFields?.[index] ?? null
    })
  }

  if (
    warehouseFormFieldsCopy.customFormFields &&
    typeof formValues.customFormFields !== 'undefined'
  ) {
    warehouseFormFieldsCopy.customFormFields.forEach((field, index) => {
      field.value = formValues?.customFormFields?.[index] ?? null
    })
  }

  if (warehouseFormFieldsCopy.refNumbers) {
    warehouseFormFieldsCopy.refNumbers.value = formValues.refNumbers ?? null
  }

  return warehouseFormFieldsCopy
}

function joinIfIsArray(value?: string | string[] | null) {
  if (Array.isArray(value)) {
    return value.join(',')
  }
  return value
}

export function SendInfoForm({
  setStep,
  fields,
  facilityTimezone,
  warehouseDetails,
  warehouseFormFields,
  shipmentUUID,
  openDockCarrierId,
  stopIndex,
  onConfirm,
  onClose,
}: {
  setStep: (step: string) => void
  fields: {
    staticFields: ScheduleFormValues
    dynamicFields: Record<string, number | string | boolean | null>
  }
  facilityTimezone: string
  warehouseDetails: OpenDockWarehouseDetails
  warehouseFormFields: OpenDockWarehouseFormFields
  shipmentUUID: string
  openDockCarrierId: string | undefined
  stopIndex: number
  onConfirm: () => void
  onClose: () => void
}) {
  const { track } = useShipmentAnalytics()

  const formikProperties = useFormik<FormValues>({
    initialValues: {
      formFields: warehouseFormFields.formFields.map(fields => fields.value),
      customFormFields: warehouseFormFields.customFormFields.map(fields => fields.value),
      refNumbers: joinIfIsArray(warehouseFormFields.refNumbers?.value),
    },
    onSubmit: () => undefined,
    validationSchema: buildValidationSchema(warehouseFormFields),
    validateOnMount: true,
  })

  const { createAppointment, status } = useCreateOpenDockWarehouseAppointment()

  useEffect(() => {
    if (status === RequestStatus.Done) {
      track('Shipment details / Appointment scheduled', {
        appointment_type: 'appt',
        loading_type: fields.staticFields.loadType?.operation,
        hadId: 'NO',
        is_opendock: true,
      })

      toast.success('Scheduled successfully.')
      onConfirm()
      setStep('instructions')
      return
    }

    if (status === RequestStatus.Error) {
      toast.error('Couldn’t schedule. Try again.')
      onClose()
      setStep('instructions')
    }
  }, [fields.staticFields.loadType?.operation, onClose, onConfirm, setStep, status, track])

  return (
    <>
      <Drawer.Body>
        <Layout.Stack gap="spacing-4" paddingB="spacing-2">
          <Steps activeStep="send-info" style={{ gap: '0' }}>
            <Steps.Step completed name="schedule">
              Schedule
            </Steps.Step>
            <Steps.Step name="send-info">Send Info</Steps.Step>
          </Steps>
          {warehouseFormFields && (
            <>
              <LabeledValue>
                <LabeledValue.Label>Warehouse</LabeledValue.Label>
                <LabeledValue.Value>{warehouseDetails?.name}</LabeledValue.Value>
              </LabeledValue>
              <LabeledValue>
                <LabeledValue.Label>Load type</LabeledValue.Label>
                <LabeledValue.Value>
                  {formatLoadTypeInfo(fields.staticFields.loadType!)}
                </LabeledValue.Value>
              </LabeledValue>
              <LabeledValue>
                <LabeledValue.Label>Dock</LabeledValue.Label>
                <LabeledValue.Value>{fields.staticFields.dock?.label}</LabeledValue.Value>
              </LabeledValue>
              <LabeledValue>
                <LabeledValue.Label>Scheduled At</LabeledValue.Label>
                <LabeledValue.Value>
                  {formatScheduledAt(
                    fields.staticFields.appointmentDate!,
                    fields.staticFields.appointmentTime!,
                    facilityTimezone
                  )}
                </LabeledValue.Value>
              </LabeledValue>
              <Layout.Stack gap="spacing-4">
                {warehouseFormFields.formFields.map((field, index) => (
                  <DynamicField
                    key={`form-fields-${index}`}
                    field={field}
                    fieldName={`formFields.${index}`}
                    formikProperties={formikProperties}
                    shipmentUUID={shipmentUUID}
                    facilityTimezone={facilityTimezone}
                  />
                ))}
                {warehouseFormFields.customFormFields.map((field, index) => (
                  <DynamicField
                    key={`custom-form-fields-${index}`}
                    field={field}
                    fieldName={`customFormFields.${index}`}
                    formikProperties={formikProperties}
                    shipmentUUID={shipmentUUID}
                    facilityTimezone={facilityTimezone}
                  />
                ))}
                {warehouseFormFields.refNumbers && (
                  <DynamicRefNumberField
                    field={warehouseFormFields.refNumbers}
                    fieldName={`refNumbers`}
                    formikProperties={formikProperties}
                  />
                )}
              </Layout.Stack>
            </>
          )}
        </Layout.Stack>
      </Drawer.Body>
      <Drawer.Actions>
        <Drawer.ActionTertiary
          onClick={() => setStep('schedule')}
          leading={<Icon name="arrow-left" color="color-text-primary" />}
          disabled={status === RequestStatus.Pending}
        >
          Back
        </Drawer.ActionTertiary>
        <Drawer.ActionPrimary
          disabled={!formikProperties.isValid}
          loading={status === RequestStatus.Pending}
          onClick={() => {
            const updatedWarehouseFormFields = setFormValuesToWarehouseFormFields(
              warehouseFormFields,
              formikProperties.values
            )

            createAppointment(shipmentUUID, stopIndex, {
              staticFields: fields.staticFields,
              warehouseFormFields: updatedWarehouseFormFields,
              openDockCarrierId,
              facilityTimezone,
            })
          }}
        >
          Confirm appointment
        </Drawer.ActionPrimary>
      </Drawer.Actions>
    </>
  )
}
